import { useEffect, useState } from 'preact/hooks';
import {
  listCommsConversations,
  listTeamMembers,
  type CommsConversation,
  type CommsMessage,
  type TeamMember,
} from '../lib/api';
import { routePath, type ChatLane } from '../routes/route';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { ChatThread } from '../components/ChatThread';
import { ChatPeople } from '../components/ChatPeople';
import { ListSearch, matches } from '../components/ListSearch';
import { ListSort, type SortDir } from '../components/ListSort';
import { useFrameOwnsBack } from '../components/FrameBack';
import { useMarkThreadRead } from '../hooks/useUnread';
import { publishThreadUnread, threadKey, unreadLabel } from '../lib/unread';
import './Conversations.css';

// S5b — THE OFFICE OPENS THE CONVERSATION (wf03 §02, blessed 2026-08-06;
// decision 817a0489). CV2 reworked three things and left the posture alone:
//
//   · the THREAD is now the shared ChatThread component (one thread, two
//     people — the tech's Chat tab renders the same one), which also moved
//     the office to NEWEST-FIRST keyset paging. S5b paged oldest-first via
//     offset; a chat opened on its oldest page is a chat opened on the
//     wrong end, and the spec explicitly allowed the change.
//   · the list gained SEARCH (name match over the loaded page) and a sort
//     toggle (recent | name) — the register's owed find/reorder item.
//   · a job thread carries the People door (components/ChatPeople): the
//     office's grants, listed and editable. It renders ONLY when the
//     admin-gated roster actually answered — the S5d enrolment-door
//     posture (no dead-end buttons, no client-side role predicate): a
//     People panel without names to offer would be a door to nowhere.
//
// ONE LIST, TWO KINDS. Job topics and direct lanes together, newest
// activity first, exactly as `GET /api/comms/conversations` returns them —
// 'Recent' renders the server's order and never re-sorts it; 'Name' is an
// explicit, visible choice.
//
// WHAT IS NOT DRAWN, AND WHY. wf03's office frame shows a fifth, locked
// row: "🔒 Tim R. — dispatch · Not your lane". THE API CANNOT PRODUCE IT
// and this screen must not invent it: `/api/comms/conversations` filters
// through core's ONE `laneVisibleTo` predicate BEFORE paginating, so an
// invisible lane is not in the response in any form. Drawing a locked row
// client-side would be manufacturing the existence of a conversation out
// of nothing. Role is the server's answer, read and never re-derived —
// there is no copy of `laneVisibleTo` in this file.
//
// PAGED, NOT SWEPT (Core register gap 11): one page of the list, one page
// of a thread; the next of either is a button.

export interface ConversationsProps {
  tenantId: string;
  /** Which conversation is open, from the route. Absent = the list alone. */
  open?: { lane: ChatLane; id: string };
  onNavigate: (path: string) => void;
}

type LoadState = 'loading' | 'ready' | 'error';

/** One page of conversations. Fifty is the route's own default and
 * comfortably more than a shop of fourteen ever has open at once. */
const PAGE = 50;

/** The lane's name in the office's own vocabulary. The PACK declares these
 * strings (`comms.lanes.office.label` = "Office"), but they are not on this
 * route's wire shape — capitalising the key is the smallest honest thing a
 * client can do with what it was given; a served label stays noted as owed. */
function laneLabel(lane: 'office' | 'dispatch'): string {
  return lane === 'office' ? 'Office' : 'Dispatch';
}

/** wf03's row mark: a work order's number, a person's initials. Never
 * invented — falls back to the first characters of whatever name there is. */
function mark(text: string): string {
  const digits = text.replace(/\D+/g, '');
  if (digits.length >= 3) return digits.slice(-4);
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
  return text.slice(0, 2).toUpperCase();
}

function keyOf(c: CommsConversation): string {
  return c.kind === 'job' ? threadKey('job', c.recordId) : threadKey(c.lane, c.membershipId);
}

function addressOf(c: CommsConversation): { lane: ChatLane; id: string } {
  return c.kind === 'job' ? { lane: 'job', id: c.recordId } : { lane: c.lane, id: c.membershipId };
}

/** Wall-clock for a row: the time today, "Yest", else the date. */
function when(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  if (sameDay) return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (
    d.getFullYear() === yesterday.getFullYear() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getDate() === yesterday.getDate()
  ) {
    return 'Yest';
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const SORTS = [
  { key: 'recent', label: 'Recent', ascWord: 'oldest first', descWord: 'newest first', defaultDir: 'desc' as SortDir },
  { key: 'name', label: 'Name', ascWord: 'A–Z', descWord: 'Z–A', defaultDir: 'asc' as SortDir },
] as const;

export function Conversations({ tenantId, open, onNavigate }: ConversationsProps) {
  // CV5 §2 — see the thread's head below.
  const frameOwnsBack = useFrameOwnsBack();
  const [load, setLoad] = useState<LoadState>('loading');
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [conversations, setConversations] = useState<CommsConversation[]>([]);
  /** Whether the LAST page came back full — the only honest signal this
   * route gives that there may be more (it returns no total). */
  const [maybeMore, setMaybeMore] = useState(false);
  const [paging, setPaging] = useState(false);
  /** userId -> roster row. `GET /api/team/members` is ADMIN-ONLY, so a
   * dispatcher gets no names — an expected outcome, never a broken screen
   * (S11's precedent). Null means "not read". */
  const [people, setPeople] = useState<Map<string, TeamMember> | null>(null);
  const [roster, setRoster] = useState<TeamMember[]>([]);
  const [retryNonce, setRetryNonce] = useState(0);

  // CV2 — find/reorder. Search filters the loaded page in memory (the
  // answer is already here and should feel like it); sort names its own
  // order out loud (ListSort's whole point).
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<string>('recent');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const now = new Date();
  const openKey = open ? `${open.lane}:${open.id}` : null;

  useEffect(() => {
    let cancelled = false;
    async function loadList() {
      setLoad((prev) => (prev === 'ready' ? 'ready' : 'loading'));
      setFailure(null);
      try {
        const page = await listCommsConversations(tenantId, { limit: PAGE, offset: 0 });
        if (cancelled) return;
        setConversations(page);
        setMaybeMore(page.length === PAGE);
        setLoad('ready');
        // CV6b §4 — the office's unread set is CV6a's OFFICE RULE, unchanged
        // and never re-derived here: a manager sees unread only on threads
        // they are actually on, because the count comes off the row the
        // server already filtered through `laneVisibleTo`.
        publishThreadUnread(
          page.map((c) => ({ key: keyOf(c), count: c.unreadCount, capped: c.unreadCountCapped }))
        );
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLoad('error');
        return;
      }
      // WHO THESE PEOPLE ARE, on its own read and its own failure: the
      // roster is admin-only and its refusal costs the NAMES (and the
      // People door, which cannot honestly render without them) — never
      // the list.
      try {
        const rows = await listTeamMembers(tenantId);
        if (cancelled) return;
        const map = new Map<string, TeamMember>();
        for (const m of rows) map.set(m.userId, m);
        setPeople(map);
        setRoster(rows);
      } catch {
        if (!cancelled) {
          setPeople(null);
          setRoster([]);
        }
      }
    }
    void loadList();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce]);

  async function loadNextPage() {
    if (paging) return;
    setPaging(true);
    try {
      const next = await listCommsConversations(tenantId, { limit: PAGE, offset: conversations.length });
      const merged = [...conversations, ...next];
      setConversations(merged);
      setMaybeMore(next.length === PAGE);
      // Republish the WHOLE loaded list — publishThreadUnread replaces
      // rather than merges (see its header).
      publishThreadUnread(
        merged.map((c) => ({ key: keyOf(c), count: c.unreadCount, capped: c.unreadCountCapped }))
      );
    } catch (err) {
      setFailure(describeReadFailure(err));
    }
    setPaging(false);
  }

  function titleOf(c: CommsConversation): string {
    if (c.kind === 'job') return c.recordName;
    const person = people?.get(c.userId);
    const name = person?.name ?? person?.email ?? null;
    // No roster read (a dispatcher's ordinary case) -> the lane names
    // itself. Never a blank, and never a guessed name.
    return name ? `${name} — ${laneLabel(c.lane)}` : `${laneLabel(c.lane)} lane`;
  }

  /** The row the list carries after a send, so the pane stays truthful
   * without a second read. */
  function noteSent(message: CommsMessage) {
    setConversations((prev) =>
      prev.map((c) =>
        keyOf(c) === openKey
          ? {
              ...c,
              lastMessage: { body: message.body, senderKind: message.senderKind, createdAt: message.createdAt },
              lastActivityAt: message.createdAt,
            }
          : c
      )
    );
  }

  // CV6b §4 — opening a thread marks it read, here exactly as on the tech's
  // side (one hook, one call, one badge).
  useMarkThreadRead(tenantId, open, (thread) => {
    setConversations((prev) =>
      prev.map((c) => (keyOf(c) === thread ? { ...c, unreadCount: 0, unreadCountCapped: false } : c))
    );
  });

  const openConversation = conversations.find((c) => keyOf(c) === openKey) ?? null;

  // Search narrows, sort orders — both over the PAGE that was read, stated
  // as such ("More conversations" is still the door to the rest).
  const shown = conversations.filter((c) => matches(query, [titleOf(c)]));
  if (sortKey === 'name') {
    shown.sort((a, b) => titleOf(a).localeCompare(titleOf(b)) * (sortDir === 'asc' ? 1 : -1));
  } else if (sortDir === 'asc') {
    shown.reverse(); // the server's MRU order, upside down — an explicit ask
  }

  const list = (
    <div class="convo__list" data-testid="convo-list">
      <div class="convo__sec">
        <span>Conversations</span>
        <span class="convo__mini">{conversations.length === 1 ? '1 open' : `${conversations.length} open`}</span>
      </div>
      <ListSearch
        value={query}
        onChange={setQuery}
        placeholder="Find a job or a person"
        label="Search conversations"
      />
      <ListSort
        options={SORTS}
        activeKey={sortKey}
        dir={sortDir}
        onChange={(key, dir) => {
          setSortKey(key);
          setSortDir(dir);
        }}
      />
      {conversations.length === 0 ? (
        <div class="convo__empty">
          No conversations yet. A job thread appears the moment a work order does, and a direct lane
          the moment someone is enrolled.
        </div>
      ) : shown.length === 0 ? (
        <div class="convo__empty" data-testid="convo-no-match">
          Nothing on this page matches “{query}”.
        </div>
      ) : (
        shown.map((c) => {
          const address = addressOf(c);
          const title = titleOf(c);
          const last = c.lastMessage;
          // CV6b §4 — the same treatment the tech's list gets, and the same
          // rule: MRU ordering is the server's and stays untouched; the mark
          // is what makes an unread thread findable.
          const unread = c.unreadCount > 0;
          return (
            <button
              type="button"
              class={`convo__row${keyOf(c) === openKey ? ' convo__row--on' : ''}${unread ? ' convo__row--unread' : ''}`}
              key={keyOf(c)}
              onClick={() => onNavigate(routePath({ screen: 'conversation', ...address }))}
            >
              <span class="convo__mark" aria-hidden="true">
                {mark(title)}
              </span>
              <span class="convo__row-tx">
                <b>{title}</b>
                <small>
                  {last
                    ? `${last.senderKind === 'system' ? 'Rexx' : last.senderKind === 'office' ? 'Office' : 'Tech'}: ${last.body}`
                    : c.kind === 'dm' && c.enrolmentStatus === 'pending'
                      ? 'Enrolled, waiting for them to connect Telegram'
                      : c.kind === 'job' && c.topicStatus !== 'created'
                        ? `Thread isn't live yet — ${c.topicStatus}`
                        : 'Nothing said yet'}
                </small>
              </span>
              <span class="convo__row-t">
                {when(c.lastActivityAt, now)}
                {unread ? (
                  <span class="convo__unread" data-testid={`convo-unread-${keyOf(c)}`}>
                    {unreadLabel({ count: c.unreadCount, capped: c.unreadCountCapped })}
                  </span>
                ) : null}
              </span>
            </button>
          );
        })
      )}
      {maybeMore ? (
        <button type="button" class="convo__more" disabled={paging} onClick={() => void loadNextPage()}>
          {paging ? 'One moment…' : 'More conversations'}
        </button>
      ) : null}
      {people === null && load === 'ready' ? (
        // The roster refusal, said once and quietly — it costs the NAMES
        // and the People door, and nothing else.
        <div class="convo__note">
          Names come from the team roster, which this account may not read. Direct lanes are shown by
          lane.
        </div>
      ) : null}
    </div>
  );

  let thread;
  if (!open) {
    thread = (
      <div class="convo__thread convo__thread--none">
        <div class="convo__empty">Pick a conversation.</div>
      </div>
    );
  } else {
    const laneName = open.lane === 'job' ? 'Job thread' : `${laneLabel(open.lane)} lane`;
    const heading = openConversation
      ? titleOf(openConversation)
      : // Deep-linked to a conversation that is not on the page we read.
        // The lane and the id are all that is honestly known.
        laneName;
    const waiting =
      openConversation?.kind === 'job' && openConversation.topicStatus !== 'created'
        ? `This job's Telegram topic is ${openConversation.topicStatus}. Nothing can be sent to it until Rexx has created it.`
        : openConversation?.kind === 'dm' && openConversation.enrolmentStatus === 'pending'
          ? 'Their number is enrolled but they have not connected Telegram yet, so a message here has nowhere to arrive.'
          : null;

    thread = (
      <div class="convo__thread">
        <div class="convo__thead">
          {/* CV5 §2 — one escape, one place: on the phone frame the way
              back is the frame's (the conversation route's parent IS
              `chat`), so this stops drawing a second one. On the office
              rail, where the frame has no top bar, it is unchanged. */}
          {frameOwnsBack ? null : (
            <button
              type="button"
              class="convo__back"
              onClick={() => onNavigate(routePath({ screen: 'chat' }))}
            >
              ‹ Conversations
            </button>
          )}
          <div class="convo__thead-tx">
            <b>{heading}</b>
            <small>{laneName}</small>
          </div>
        </div>

        <ChatThread
          tenantId={tenantId}
          lane={open.lane}
          id={open.id}
          viewerKind="office"
          notice={waiting}
          onSent={noteSent}
          extra={
            // §4 — the People door, on a JOB thread, and only when the
            // roster answered (see the header: no dead-end buttons).
            open.lane === 'job' && people !== null ? (
              <ChatPeople tenantId={tenantId} recordId={open.id} roster={roster} />
            ) : null
          }
        />
      </div>
    );
  }

  if (load === 'loading') {
    return (
      <div class="convo">
        <div class="convo__eyebrow">Conversations</div>
        <div class="convo__say">One moment…</div>
      </div>
    );
  }

  if (load === 'error' && failure !== null) {
    return (
      <div class="convo">
        <div class="convo__eyebrow">Conversations</div>
        <div class="convo__say" data-testid="convo-list-failure">
          {failure.headline}
          <br />
          <span class="convo__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="convo__more" onClick={() => setRetryNonce((n) => n + 1)}>
            Read it again
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div class={`convo${open ? ' convo--open' : ''}`}>
      <div class="convo__grid">
        {list}
        {thread}
      </div>
    </div>
  );
}
